import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, query, collection, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Super admin phone numbers (get unlimited credits)
// Add phone numbers WITH country code (e.g., +917378957088)
const SUPER_ADMIN_PHONES = [
  '+917378957088',
  '+917478957011',
  '+918308346346',
  '7378957088',
  '7478957011',
  '8308346346',
];

// Helper function to check if phone is super admin
function checkIsSuperAdmin(phoneNumber) {
  if (!phoneNumber) return false;
  
  // Normalize phone number - remove spaces, dashes
  const normalizedPhone = phoneNumber.replace(/[\s\-]/g, '');
  
  // Check various formats
  for (const adminPhone of SUPER_ADMIN_PHONES) {
    const normalizedAdmin = adminPhone.replace(/[\s\-]/g, '');
    
    // Direct match
    if (normalizedPhone === normalizedAdmin) return true;
    
    // Match without country code
    if (normalizedPhone.endsWith(normalizedAdmin)) return true;
    if (normalizedAdmin.endsWith(normalizedPhone)) return true;
    
    // Match last 10 digits
    const phoneLast10 = normalizedPhone.slice(-10);
    const adminLast10 = normalizedAdmin.slice(-10);
    if (phoneLast10 === adminLast10) return true;
  }
  
  return false;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Setup invisible recaptcha
  async function setupRecaptcha(elementId) {
    try {
      // Clear existing verifier if any
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          console.log('Clear recaptcha error:', e);
        }
        window.recaptchaVerifier = null;
      }
      
      // Create new verifier
      window.recaptchaVerifier = new RecaptchaVerifier(auth, elementId, {
        size: 'invisible',
        callback: (response) => {
          console.log('Recaptcha verified successfully');
        },
        'expired-callback': () => {
          console.log('Recaptcha expired');
          window.recaptchaVerifier = null;
        }
      });
      
      // Render the recaptcha - this is important!
      await window.recaptchaVerifier.render();
      console.log('Recaptcha rendered successfully');
      
      return window.recaptchaVerifier;
    } catch (error) {
      console.error('Setup recaptcha error:', error);
      window.recaptchaVerifier = null;
      throw error;
    }
  }

  // Clear recaptcha
  function clearRecaptcha() {
    try {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } catch (e) {
      console.log('Clear recaptcha error:', e);
      window.recaptchaVerifier = null;
    }
  }

  // Check if user exists by phone
  async function checkUserExists(phone) {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('phone', '==', phone));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return snapshot.docs[0].data();
    }
    return null;
  }

  // Verify password for existing user
  async function verifyPassword(phone, password) {
    const user = await checkUserExists(phone);
    if (!user) {
      throw new Error('User not found');
    }
    // Simple password check (in production, use proper hashing)
    if (user.passwordHash !== btoa(password)) {
      throw new Error('Invalid password');
    }
    return user;
  }

  // Send OTP for login (verify password after OTP confirmation)
  async function sendLoginOTP(phone, password, recaptchaVerifier) {
    // Store password temporarily to verify after OTP
    window.tempLoginPassword = password;
    window.tempLoginPhone = phone;
    
    // Send OTP (don't verify password yet - user not authenticated)
    const confirmationResult = await signInWithPhoneNumber(auth, phone, recaptchaVerifier);
    return confirmationResult;
  }

  // Send OTP for signup (skip pre-check, handle duplicates after OTP)
  async function sendSignupOTP(phone, recaptchaVerifier) {
    // Send OTP first (don't check Firestore - user not authenticated yet)
    const confirmationResult = await signInWithPhoneNumber(auth, phone, recaptchaVerifier);
    return confirmationResult;
  }

  // Send OTP for password reset (verify user exists after OTP)
  async function sendPasswordResetOTP(phone, recaptchaVerifier) {
    // Send OTP first (can't verify user exists before auth)
    const confirmationResult = await signInWithPhoneNumber(auth, phone, recaptchaVerifier);
    return confirmationResult;
  }

  // Update password for user
  async function updatePassword(phone, newPassword) {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('phone', '==', phone));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('User not found');
    }
    
    const userDoc = snapshot.docs[0];
    await updateDoc(doc(db, 'users', userDoc.id), {
      passwordHash: btoa(newPassword),
      updatedAt: serverTimestamp()
    });
    
    return true;
  }

  // Complete signup after OTP verification
  async function completeSignup(user, userData) {
    const { name, phone, password } = userData;
    
    // Check if user profile already exists (returning user)
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      // User already exists - just load their profile
      await loadUserProfile(user);
      return;
    }
    
    // New user - create organization and profile
    const orgRef = doc(db, 'organizations', user.uid);
    await setDoc(orgRef, {
      id: user.uid,
      name: name + "'s Clinic",
      phone: phone,
      plan: 'free',
      creditsTotal: 5,
      creditsUsed: 0,
      settings: {
        allowDoctorInvites: true,
        requireApproval: false
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Create user profile
    await setDoc(userRef, {
      id: user.uid,
      phone: phone,
      name: name,
      passwordHash: btoa(password), // Simple encoding (use bcrypt in production)
      organizationId: user.uid,
      organizationName: name + "'s Clinic",
      role: 'admin',
      specialization: '',
      licenseNumber: '',
      diagnosesCount: 0,
      isActive: true,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp()
    });

    await loadUserProfile(user);
  }

  // Logout
  async function logout() {
    clearRecaptcha();
    await signOut(auth);
    setUserProfile(null);
    setIsSuperAdmin(false);
  }

  // Load user profile from Firestore
  async function loadUserProfile(user) {
    if (!user) {
      setUserProfile(null);
      setIsSuperAdmin(false);
      return;
    }

    // Check if super admin FIRST (regardless of Firestore document)
    const isSuper = checkIsSuperAdmin(user.phoneNumber);
    console.log('Super admin check:', { phoneNumber: user.phoneNumber, isSuper });
    setIsSuperAdmin(isSuper);

    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        
        // Load organization data
        if (userData.organizationId) {
          const orgRef = doc(db, 'organizations', userData.organizationId);
          const orgSnap = await getDoc(orgRef);
          if (orgSnap.exists()) {
            userData.organization = orgSnap.data();
          }
        }

        setUserProfile(userData);

        // Update last login
        await updateDoc(userRef, {
          lastLoginAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }

  // Refresh user profile
  async function refreshProfile() {
    if (currentUser) {
      await loadUserProfile(currentUser);
    }
  }

  // Save diagnosis and deduct credits
  async function saveDiagnosis(diagnosisData) {
    if (!currentUser || !userProfile) {
      throw new Error('User not authenticated');
    }

    const creditsNeeded = diagnosisData.modelsUsed?.length || 1;
    
    // Super admins have unlimited credits
    if (!isSuperAdmin) {
      const creditsRemaining = (userProfile.organization?.creditsTotal || 0) - (userProfile.organization?.creditsUsed || 0);
      if (creditsRemaining < creditsNeeded) {
        throw new Error('Insufficient credits. Please upgrade your plan.');
      }
    }

    // Save diagnosis (implementation in database.js)
    // Deduct credits from organization
    if (!isSuperAdmin && userProfile.organizationId) {
      const orgRef = doc(db, 'organizations', userProfile.organizationId);
      await updateDoc(orgRef, {
        creditsUsed: (userProfile.organization?.creditsUsed || 0) + creditsNeeded,
        updatedAt: serverTimestamp()
      });

      // Update user's diagnosis count
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        diagnosesCount: (userProfile.diagnosesCount || 0) + 1
      });

      // Refresh profile to get updated credits
      await refreshProfile();
    }

    return true;
  }

  // Check credits before diagnosis
  function checkCredits(modelsCount) {
    if (isSuperAdmin) return true;
    
    const creditsRemaining = (userProfile?.organization?.creditsTotal || 0) - (userProfile?.organization?.creditsUsed || 0);
    return creditsRemaining >= modelsCount;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user);
      } else {
        setUserProfile(null);
        setIsSuperAdmin(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    isSuperAdmin,
    loading,
    setupRecaptcha,
    clearRecaptcha,
    checkUserExists,
    sendLoginOTP,
    sendSignupOTP,
    sendPasswordResetOTP,
    updatePassword,
    completeSignup,
    logout,
    refreshProfile,
    saveDiagnosis,
    checkCredits
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
